/**
 * BlockPolicyContext — Phase 3 (Drive-First Pivot).
 *
 * Single source of truth for "is this user blocked from doing X right now?"
 * Backed by GET /api/subscriptions/me/ which returns a `restrictions` object:
 *
 *   {
 *     upload_document: { allowed, reason, message, hint_action, cta_url, severity, metadata },
 *     upload_voice:    { ... },
 *     upload_text:     { ... },
 *     download:        { ... },
 *     view:            { ... },
 *     stream_voice:    { ... },
 *     is_superadmin:   bool,
 *   }
 *
 * Why a context instead of a per-component hook: every page that renders an
 * Upload / Download / View / Play button needs to know the block state. We
 * don't want each of them firing its own request — fetch once, share.
 *
 * Refresh strategy:
 *   - Initial fetch on mount
 *   - Poll every 60s (cheap; one GET; subscription state moves on the order of days)
 *   - Manual `reload()` exposed for after-checkout / after-disconnect events
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { subscriptionAPI } from '../services/subscription'
import { useAuth } from './AuthContext'

const DEFAULT_RESTRICTIONS = {
  upload_document: { allowed: true },
  upload_voice: { allowed: true },
  upload_text: { allowed: true },
  download: { allowed: true },
  view: { allowed: true },
  stream_voice: { allowed: true },
  is_superadmin: false,
}

const BlockPolicyContext = createContext({
  restrictions: DEFAULT_RESTRICTIONS,
  loading: true,
  error: null,
  reload: () => {},
})

const POLL_INTERVAL_MS = 60 * 1000

export function BlockPolicyProvider({ children }) {
  const { isAuthenticated, user } = useAuth()
  const [restrictions, setRestrictions] = useState(DEFAULT_RESTRICTIONS)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const reload = useCallback(async () => {
    if (!isAuthenticated) {
      // Without auth there's nothing to gate — pass everything through.
      setRestrictions(DEFAULT_RESTRICTIONS)
      setLoading(false)
      return
    }
    try {
      const res = await subscriptionAPI.me()
      const payload = res?.data?.data ?? res?.data ?? null
      if (res?.success && payload?.restrictions) {
        setRestrictions(payload.restrictions)
        setError(null)
      } else {
        // No restrictions key → server is old or user has no workspace; fail open.
        setRestrictions(DEFAULT_RESTRICTIONS)
      }
    } catch (e) {
      // Network error → fail open so the UI doesn't lock the user out.
      setError(e?.message || 'Failed to load')
      setRestrictions(DEFAULT_RESTRICTIONS)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    setLoading(true)
    reload()
  }, [reload, user?.id])

  useEffect(() => {
    if (!isAuthenticated) return undefined
    const t = setInterval(reload, POLL_INTERVAL_MS)
    return () => clearInterval(t)
  }, [reload, isAuthenticated])

  return (
    <BlockPolicyContext.Provider value={{ restrictions, loading, error, reload }}>
      {children}
    </BlockPolicyContext.Provider>
  )
}

export function useBlockPolicy() {
  return useContext(BlockPolicyContext)
}

/**
 * Convenience selector — returns the single GateResult for an action.
 * Example:
 *   const gate = useActionGate('upload_document')
 *   if (!gate.allowed) showBlocked(gate.message)
 */
export function useActionGate(action) {
  const { restrictions, loading } = useBlockPolicy()
  const result = restrictions?.[action]
  if (!result) return { allowed: true, loading }
  return { ...result, loading }
}

export default BlockPolicyContext
